<template>
  <!-- 기공소용 동영상 가이드 재생 버튼(공용).
       클릭 시 언어(한국어/中文) 드롭다운이 열리고, 선택하면 텐센트 COS에 올려둔 mp4를
       새 탭에서 연다. 헤더의 앱 언어 전환과는 무관하게 파일만 고른다.
       작업지시서 생성 화면·수기 작업지시서 목록 등 여러 화면에서 GuideDownloadButton과 나란히 쓴다.

       왜 COS인가: 시청자가 대부분 중국 내 기공소다. 빌드 번들(same-origin)에 담기엔
       파일이 150MB 이상이라 배포마다 번들이 커지고, 국제 구간을 타면 재생이 끊긴다.
       ap-shanghai 공개읽기 버킷에서 직접 받으면 중국 국내망으로 붙는다.

       왜 다운로드가 아니라 새 탭인가: 파일이 커서 전부 받기 전에는 볼 수 없다.
       새 탭이면 스트리밍으로 즉시 재생되고, 저장이 필요하면 브라우저에서 우클릭으로 받을 수 있다. -->
  <a-dropdown
    :trigger="['click']"
    placement="bottomRight"
  >
    <button
      type="button"
      class="guide-dl-btn"
      @click.prevent
    >
      <i class="fa-solid fa-circle-play"></i>
      {{ $t('workorder_guide_video_btn') }}
      <i class="fa-solid fa-chevron-down guide-dl-caret"></i>
    </button>
    <template #overlay>
      <a-menu @click="onMenuClick">
        <a-menu-item key="zh">
          <i class="fa-solid fa-video guide-dl-ico"></i> 中文
        </a-menu-item>
        <a-menu-item key="ko">
          <i class="fa-solid fa-video guide-dl-ico"></i> 한국어
        </a-menu-item>
      </a-menu>
    </template>
  </a-dropdown>
</template>

<script>
// SSOT 매니페스트를 런타임(COS 포인터)에서 읽는다 — 가이드 개정 시 앱 재배포 불필요.
// (SPEC-CHINA-WO-GUIDE-SSOT-001 v0.4.0)
import { getGuideManifest } from '@/utils/guideManifest';

// COS 공개읽기 버킷의 가이드 자산 루트.
// 환경변수로 덮어쓸 수 있게 두되, 미설정 시에도 동작하도록 운영 버킷을 기본값으로 둔다
// (이 화면은 세 환경 모두 같은 파일을 본다 — 가이드 영상은 환경별로 다르지 않다).
const DEFAULT_BASE = 'https://dentalmax-guides-1326561935.cos.ap-shanghai.myqcloud.com';

// manifest가 깨져도 버튼은 죽지 않아야 하므로 기존 하드코딩 경로를 폴백으로 유지한다.
const FALLBACK_KEYS = {
  zh: 'videos/guide-zh.mp4',
  ko: 'videos/guide-ko.mp4'
};

export default {
  name: 'VideoGuideButton',
  methods: {
    // 드롭다운에서 언어 선택 시 호출. key는 'zh' | 'ko'.
    onMenuClick({ key }) {
      this.openVideo(key);
    },
    async openVideo(lang) {
      const guideManifest = await getGuideManifest();
      const manifestVideo = (guideManifest && guideManifest.artifacts && guideManifest.artifacts.video) || {};
      // manifest files 예: ["videos/guide-zh.mp4", "videos/guide-ko-v0.6.mp4"] —
      // 버전 접미가 붙어도 언어 토큰(guide-zh/guide-ko)으로 해석되게 includes 매칭.
      const files = Array.isArray(manifestVideo.files) ? manifestVideo.files : [];
      const key = files.find((f) => typeof f === 'string' && f.includes(`guide-${lang}`))
        || FALLBACK_KEYS[lang] || FALLBACK_KEYS.zh;
      // 우선순위: 환경변수 > manifest base > 하드코딩 기본값 (기존 env 우선순위 불변)
      const base = (process.env.VUE_APP_GUIDE_VIDEO_BASE_URL || manifestVideo.base || DEFAULT_BASE).replace(/\/$/, '');
      // noopener/noreferrer — 새 탭이 window.opener로 이 앱을 조작하지 못하게 막는다.
      window.open(`${base}/${key}`, '_blank', 'noopener,noreferrer');
    }
  }
};
</script>

<style scoped lang="scss">
// GuideDownloadButton(.guide-dl-btn)과 완전히 같은 룩으로 통일한다.
// 두 버튼이 나란히 서므로 한쪽만 달라 보이면 눈에 띈다.
$Gray-10: #F1F2F3;
$Gray-30: #DDE0E4;
$Gray-50: #ACB2B9;
$Gray-60: #8D939A;
$Gray-80: #4B5158;
$Gray-90: #2F3337;

.guide-dl-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid $Gray-30;
  border-radius: 8px;
  background: #fff;
  color: $Gray-80;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  i {
    font-size: 14px;
    color: $Gray-60;
  }

  &:hover {
    background: $Gray-10;
    border-color: $Gray-50;
    color: $Gray-90;

    i {
      color: $Gray-80;
    }
  }

  .guide-dl-caret {
    font-size: 10px;
    margin-left: 2px;
  }
}

.guide-dl-ico {
  color: $Gray-60;
  margin-right: 4px;
}
</style>
